import { useState } from 'react';
import { ILeaveRequest } from '../types/leave';
import { useUpdateLeaveRequestStatus } from '../hooks/mutations';
import { BtnDefault, BtnPrimary } from './common/Buttons';
import { formatTimeHandler } from '../utils';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '../state/auth';
import { fetchAgentLeaveBalance } from '../hooks/queries';

interface LeaveRequestDetailsProps {
  leaveRequest?: ILeaveRequest;
  onUpdate: () => void;
  onUpdateError: () => void;
}

const LeaveRequestDetails = ({
  leaveRequest,
  onUpdate,
  onUpdateError,
}: LeaveRequestDetailsProps) => {
  const [comment, setComment] = useState('');
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  const { data: balanceData } = fetchAgentLeaveBalance(
    leaveRequest?.email ?? '',
    leaveRequest?.account ?? ''
  );
  const availableHours = balanceData?.data?.balance?.[leaveRequest?.type ?? '']?.available;

  const { mutate: updateStatus, isPending } = useUpdateLeaveRequestStatus({
    onSuccess: () => {
      setLoadingAction(null);
      onUpdate();
    },
    onError: () => {
      setLoadingAction(null);
      onUpdateError();
    },
  });

  if (!leaveRequest) return null;

  const handleAction = (
    status: 'approved' | 'rejected' | 'request-for-info'
  ) => {
    if (!leaveRequest._id) return;

    setLoadingAction(status);
    updateStatus({
      leaveRequestId: leaveRequest._id,
      status,
      comment: comment || undefined,
    });
  };

  const isActionable =
    leaveRequest.status === 'pending' ||
    leaveRequest.status === 'request-for-info';

  return (
    <div className="p-6 space-y-6">
      {/* Status Badge */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Leave Request</h3>
        <span
          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
            leaveRequest.status === 'pending'
              ? 'bg-yellow-100 text-yellow-800'
              : leaveRequest.status === 'approved'
                ? 'bg-green-100 text-green-800'
                : leaveRequest.status === 'rejected'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-blue-100 text-blue-800'
          }`}
        >
          {leaveRequest.status.toUpperCase()}
        </span>
      </div>

      {/* Leave Details */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-600">
              Agent ID
            </label>
            <p className="text-base text-gray-900">{leaveRequest.kimId}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Account</label>
            <p className="text-base text-gray-900">{leaveRequest.account}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">
              Leave Type
            </label>
            <p className="text-base text-gray-900 capitalize">
              {leaveRequest.type}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">
              Total Hours
            </label>
            <p className="text-base text-gray-900">{leaveRequest.duration}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">
              Available Balance
            </label>
            <p
              className={`text-base font-medium ${
                availableHours === undefined
                  ? 'text-gray-400'
                  : availableHours >= leaveRequest.duration
                    ? 'text-green-700'
                    : 'text-red-600'
              }`}
            >
              {availableHours === undefined
                ? '—'
                : `${availableHours}h`}
              {availableHours !== undefined && availableHours < leaveRequest.duration && (
                <span className="ml-2 text-xs text-red-500">(insufficient)</span>
              )}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">
              Start Date & Time
            </label>
            <p className="text-base text-gray-900">
              {formatTimeHandler(String(leaveRequest.startTime), leaveRequest.timeZone, 'MMM DD, YYYY hh:mm A')}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">
              End Date & Time
            </label>
            <p className="text-base text-gray-900">
              {formatTimeHandler(String(leaveRequest.stopTime), leaveRequest.timeZone, 'MMM DD, YYYY hh:mm A')}
            </p>
          </div>
        </div>

        {leaveRequest.comment && (
          <div>
            <label className="text-sm font-medium text-gray-600">
              Existing Comment
            </label>
            <p className="text-base text-gray-900 bg-gray-50 p-3 rounded">
              {leaveRequest.comment}
            </p>
          </div>
        )}

        {leaveRequest.reviewedBy && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">
                Reviewed By
              </label>
              <p className="text-base text-gray-900">
                {leaveRequest.reviewedBy.name}
              </p>
            </div>
            {leaveRequest.reviewedAt && (
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Reviewed At
                </label>
                <p className="text-base text-gray-900">
                  {formatTimeHandler(String(leaveRequest.reviewedAt), leaveRequest.timeZone, 'MMM DD, YYYY hh:mm A')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Section */}
      {isActionable && (
        <div className="border-t pt-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Add Comment (Optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:outline-none"
              rows={3}
              placeholder="Add a comment about this leave request..."
            />
          </div>

          <div className="flex gap-3">
            <BtnPrimary
              onClick={() => handleAction('approved')}
              disabled={isPending}
              className="flex items-center gap-2"
            >
              {loadingAction === 'approved' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CheckCircle size={16} />
              )}
              Approve
            </BtnPrimary>

            <BtnDefault
              onClick={() => handleAction('rejected')}
              disabled={isPending}
              className="flex items-center gap-2 !bg-red-100 !text-red-700 hover:!bg-red-200"
            >
              {loadingAction === 'rejected' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <XCircle size={16} />
              )}
              Reject
            </BtnDefault>

            <BtnDefault
              onClick={() => handleAction('request-for-info')}
              disabled={isPending}
              className="flex items-center gap-2 !bg-yellow-100 !text-yellow-700 hover:!bg-yellow-200"
            >
              {loadingAction === 'request-for-info' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <AlertCircle size={16} />
              )}
              Request Info
            </BtnDefault>
          </div>
        </div>
      )}

      {!isActionable && (
        <div className="border-t pt-6">
          <p className="text-sm text-gray-600">
            This leave request has already been {leaveRequest.status}.
          </p>
        </div>
      )}
    </div>
  );
};

export default LeaveRequestDetails;
